import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { StatsSectionDTO } from '@lib/strapi/dtos/components/sections/statsSection.dto';

/**
 * The «Γιατί Physiowave» trust band (artifact .m-why.band.edge-both):
 * FULL-BLEED violet-700 — `.bleed` breaks it out of the boxed .shell column
 * so the organic curve spans the whole viewport instead of stopping at the
 * content edge — organic on BOTH edges (.edge-both; edge-top + edge-footer
 * stacked was a bug: both set only top radii, leaving a straight bottom).
 * Headline figures in white at 8.62 (AAA); per D44 the band is its own
 * separator, so no rules around it. Content stays aligned to the shell.
 */
const StatsSectionImplementation = ({ section }: ComponentProps<StatsSectionDTO>) => {
  if (!section?.stats?.length) return null;

  return (
    // Designer pass 2026-09-14 (strip.psd): the trust band is a giant ellipse
    // BLOB, not an edge-curved full-width band — slightly wider than the
    // viewport so its sides clip gently, content on the shell grid inside.
    // stroke-10 (the designer's «21 brush 10») scribbles the right half.
    <section className="bleed relative my-14 md:my-20">
      {/* PSD aspect: the shape is 2998x1157 → the band stands ~38.6vw tall
          at full bleed; content centers inside it like the design */}
      <div className="stats-blob relative flex flex-col justify-center overflow-hidden bg-violet-700 py-20 text-white min-[1200px]:min-h-[38.6vw] min-[1200px]:py-0">
        {/* PSD geometry: the scribble spans x 46–84%, bleeding past the
            shape's bottom — the mask clips it exactly like the design */}
        <img loading="lazy" src="/images/brand/stroke-10.svg" alt="" aria-hidden="true"
          className="pointer-events-none absolute right-[14%] top-2 w-[38vw] max-w-[38rem] select-none opacity-[0.16] brightness-0 invert" />
        <div className="shell relative">
        {section.eyebrow ? (
          <p className="text-xs font-extrabold tracking-[0.08em] text-white opacity-75">{section.eyebrow}</p>
        ) : null}
        {section.heading ? (
          <h2 className="mt-2 max-w-xl text-2xl font-extrabold leading-tight text-white md:text-3xl">
            {section.heading}
          </h2>
        ) : null}
        <dl className="mt-8 grid grid-cols-2 gap-8 md:grid-cols-4">
          {section.stats.map((stat, i) => (
            <div key={i}>
              <dd className="text-3xl font-extrabold tabular-nums text-white md:text-4xl">{stat.value}</dd>
              {/* the labels ARE the claims (5-star, ΕΟΠΥΥ) — 16px at 90%,
                  not a 14px whisper (owner, 2026-09-10) */}
              <dt className="mt-1.5 text-base leading-snug text-white opacity-90">{stat.label}</dt>
            </div>
          ))}
        </dl>
        </div>
      </div>
    </section>
  );
};

export default StatsSectionImplementation;
